import type { RigoApi } from "../contract";
import type * as T from "../types";
import type { components } from "./schema";
import { ROLE_NAMES, ROLE_PERMISSIONS } from "@/lib/permissions";
import type { ApiError, SubscriptionStatus } from "../types";
import { API_ROUTES, EMAIL_ROUTES, UNSERVED } from "./routes";
import { toSession, type OrganisationWire, type SessionWire } from "./session";
import * as bills from "./bills";
import * as dashboards from "./dashboards";
import * as sales from "./sales";
import * as reports from "./reports";
import * as setup from "./setup";
import { isSuspendedResponse, notifySubscriptionSuspended } from "./suspended";

type Schemas = components["schemas"];

export class HttpError extends Error {
  constructor(public readonly problem: ApiError) {
    // `detail` first: RFC 9457 §3.1.2 says `title` describes the problem *type* and is the
    // same for every occurrence ("That could not be done"), while `detail` is what went
    // wrong this time ("This bill has already been approved with that key."). A refusal of
    // particular fields says why in `errors`, and its `detail` only says that some were refused.
    const fields = Object.values(problem.errors ?? {}).flat();
    super(fields.length > 0 ? fields.join(" ") : (problem.detail ?? problem.title));
  }
}

/**
 * The antiforgery pair the API issues from `/bff/user` (ADR-0004): `__Host-rigo.accounting.xsrf`
 * deployed, `rigo.accounting.xsrf` over plain HTTP locally. Never the conventional `XSRF-TOKEN` —
 * production RigoHR sets that one on `.rigohr.com`, so a browser signed in to RigoHR sends it here
 * too, and echoing it got every write refused.
 */
/** One month of a year, as the year-end preview names it. */
const monthFrom = (period: Schemas["PeriodStillOpen"]) => ({
  periodId: period.periodId,
  ordinal: Number(period.ordinal),
  startsOn: period.startsOn,
  endsOn: period.endsOn,
  status: Number(period.status),
});

const changeFrom = (change: Schemas["YearCloseChange"]): T.YearCloseChange => ({
  accountId: change.accountId,
  code: change.code,
  name: change.name,
  before: String(change.before),
  after: String(change.after),
});

const closureFrom = (closure: Schemas["YearClosure"]): T.YearClosure => ({
  method: closure.method as T.YearEndMethod,
  journalId: closure.journalId ?? null,
  journalNumber: closure.journalNumber ?? null,
  closedAt: closure.closedAt,
  version: Number(closure.version),
  changes: (closure.changes ?? []).map(changeFrom),
});

const XSRF_COOKIES = ["__Host-rigo.accounting.xsrf", "rigo.accounting.xsrf"];
const XSRF_HEADER = "X-XSRF-TOKEN";

/**
 * The antiforgery token, read from the cookie the API set. It is deliberately not
 * HttpOnly: echoing it back is the whole cookie-to-header pattern, and a cross-origin
 * page cannot read it, so it cannot produce the header.
 */
function antiforgeryToken(): string | undefined {
  const cookies = new Map<string, string>();
  for (const pair of document.cookie.split("; ")) {
    const eq = pair.indexOf("=");
    if (eq > 0 && !cookies.has(pair.slice(0, eq)))
      cookies.set(pair.slice(0, eq), pair.slice(eq + 1));
  }
  for (const name of XSRF_COOKIES) {
    const value = cookies.get(name);
    if (value !== undefined) return decodeURIComponent(value);
  }
  return undefined;
}

/**
 * Operations that allocate something which cannot be taken back — a statutory number, a journal.
 * Each carries an `Idempotency-Key`, because a retry after a timeout would otherwise issue a second
 * document and the sequence is gapless by law (ADR-0008). The API requires the header on these and
 * refuses without it; it is never defaulted server-side, so it has to be sent here.
 */
const ALLOCATES_A_NUMBER = /\/(issue|posting|approval|payments|credits|year-end|movements)(\?|$)/;

/**
 * Asks `/bff/user` for the antiforgery pair, once.
 *
 * The API sets the readable antiforgery cookie there and nowhere else, and validates the header on
 * every unsafe `/api/*` call. The SPA never called it, so every write it made would have been
 * refused with a 400 whose cause is in a different file. Lazy rather than on module load: a page
 * that only reads should not need a session handshake, and the promise is cached so a burst of
 * writes performs one.
 */
function createAntiforgery() {
  let pending: Promise<void> | undefined;

  return () => {
    if (antiforgeryToken() !== undefined) return Promise.resolve();
    pending ??= fetch("/bff/user", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(() => undefined)
      // A failure here is not fatal on its own: the request that follows carries whatever cookie
      // exists and the API gives the real answer. Retrying the handshake on the next write is more
      // useful than caching a rejection.
      .catch(() => {
        pending = undefined;
      });
    return pending;
  };
}

/**
 * fetch wrapper: same-origin, cookie-authenticated (ADR-0004), JSON in/out,
 * RFC 9457 problem details on error, antiforgery header for unsafe methods.
 */
export function createHttpClient(base: string) {
  const ensureAntiforgery = createAntiforgery();

  async function request<R>(
    method: string,
    path: string,
    body?: unknown,
    wantsFile = false,
  ): Promise<R> {
    if (method !== "GET") await ensureAntiforgery();
    const token = method === "GET" ? undefined : antiforgeryToken();
    const idempotencyKey =
      method === "POST" && ALLOCATES_A_NUMBER.test(path) ? crypto.randomUUID() : undefined;
    // A FormData body is multipart: the browser writes the Content-Type with its boundary.
    const form = body instanceof FormData ? body : undefined;
    const res = await fetch(base + path, {
      method,
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        ...(body !== undefined && form === undefined ? { "Content-Type": "application/json" } : {}),
        ...(token !== undefined ? { [XSRF_HEADER]: token } : {}),
        ...(idempotencyKey !== undefined ? { "Idempotency-Key": idempotencyKey } : {}),
      },
      body: form ?? (body !== undefined ? JSON.stringify(body) : undefined),
    });
    if (res.status === 204) return undefined as R;
    if (res.ok && wantsFile) return (await res.blob()) as R;
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      const problem = (data as ApiError | null) ?? {
        type: "about:blank",
        title: res.statusText,
        status: res.status,
      };
      // The subscription gate's 402. Announced before it is thrown, so the shell has swapped
      // itself for the suspended screen by the time the screen that asked handles the rejection —
      // and the customer reads one explanation rather than a dozen failed panels.
      if (isSuspendedResponse(res.status, (data as ApiError | null)?.type)) {
        notifySubscriptionSuspended();
      }
      throw new HttpError(problem);
    }
    return data as R;
  }
  const qs = (q: object) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(q))
      if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
    const s = p.toString();
    return s ? "?" + s : "";
  };
  return {
    get: <R>(path: string, q?: object) => request<R>("GET", path + (q ? qs(q) : "")),
    post: <R>(path: string, body?: unknown) => request<R>("POST", path, body),
    postForm: <R>(path: string, form: FormData) => request<R>("POST", path, form),
    postForFile: (path: string, body: unknown) => request<Blob>("POST", path, body, true),
    getFile: (path: string) => request<Blob>("GET", path, undefined, true),
    put: <R>(path: string, body?: unknown) => request<R>("PUT", path, body),
    del: <R>(path: string) => request<R>("DELETE", path),
  };
}

/** A route with its one `{param}` filled, escaped. */
const APPROVAL_DOCUMENT_TYPES: Record<T.ApprovalDocumentKind, string> = {
  invoice: "sales_invoice",
  creditNote: "credit_note",
  bill: "supplier_bill",
  debitNote: "supplier_debit_note",
  fundTransfer: "fund_transfer",
  // Listed before payment, so a supplier_payment reads back as a payment. The queue names these itself.
  employeePayment: "supplier_payment",
  payment: "supplier_payment",
  purchaseOrder: "purchase_order",
  journal: "manual_journal",
  yearClose: "year_close",
};

const APPROVAL_KINDS = {
  ...Object.fromEntries(
    Object.entries(APPROVAL_DOCUMENT_TYPES).map(([kind, type]) => [type, kind]),
  ),
  employee_payment: "employeePayment",
} as Record<string, T.ApprovalDocumentKind>;

const SUBMISSION_ROUTES: Record<T.ApprovalDocumentKind, string> = {
  invoice: API_ROUTES.submitInvoice,
  creditNote: API_ROUTES.submitCreditNote,
  bill: API_ROUTES.submitBill,
  debitNote: API_ROUTES.submitDebitNote,
  fundTransfer: API_ROUTES.submitFundTransfer,
  employeePayment: API_ROUTES.submitPayment,
  payment: API_ROUTES.submitPayment,
  purchaseOrder: API_ROUTES.submitPurchaseOrder,
  journal: API_ROUTES.submitJournal,
  yearClose: API_ROUTES.submitYearClose,
};

const toEmployeeMovement = (wire: Schemas["EmployeeMovementResponse"]): T.EmployeeMovement => ({
  id: wire.id,
  partyId: wire.partyId,
  partyName: wire.partyName,
  kind: wire.kind as T.EmployeeMovement["kind"],
  controlRole: wire.controlRole as T.EmployeeMovement["controlRole"],
  amount: String(wire.amount),
  date: wire.date,
  cashAccountName: wire.cashAccountName,
  reference: wire.reference ?? null,
  note: wire.note ?? null,
  number: wire.number ?? null,
});

const toTemplateSummary = (wire: Schemas["ReportTemplateSummary"]): T.ReportTemplateSummary => ({
  code: wire.code,
  frameworkCode: wire.frameworkCode,
  statement: wire.statement as T.ReportTemplateSummary["statement"],
  name: wire.name,
  version: wire.version,
  supplied: wire.supplied,
});

const toTemplateDetail = (wire: Schemas["ReportTemplateDetail"]): T.ReportTemplateDetail => ({
  summary: toTemplateSummary(wire.summary),
  lines: wire.lines.map((line) => ({
    code: line.code,
    kind: line.kind as T.TemplateLine["kind"],
    label: line.label,
    tags: [...line.tags],
    formula: [...line.formula],
    negate: line.negate,
    strong: line.strong,
    tie: line.tie ?? null,
  })),
});

const toRenderedStatement = (wire: Schemas["RenderedStatement"]): T.RenderedStatement => ({
  templateCode: wire.templateCode,
  templateName: wire.templateName,
  templateVersion: wire.templateVersion,
  from: wire.from ?? null,
  to: wire.to,
  compareFrom: wire.compareFrom ?? null,
  compareTo: wire.compareTo ?? null,
  lines: wire.lines.map((line) => ({
    code: line.code,
    kind: line.kind as T.ReportLine["kind"],
    label: line.label,
    strong: line.strong,
    amount: line.amount === null || line.amount === undefined ? null : String(line.amount),
    compare: line.compare === null || line.compare === undefined ? null : String(line.compare),
    suggestedAccounts: Number(line.suggestedAccounts),
    accounts: line.accounts.map((account) => ({
      accountId: account.accountId,
      code: account.code,
      name: account.name,
      tagCode: account.tagCode ?? null,
      status: account.status.toLowerCase() as T.AccountMappingRow["status"],
      amount: String(account.amount),
    })),
  })),
  unmapped: wire.unmapped.map((row) => ({ ...row, amount: String(row.amount) })),
  excluded: wire.excluded.map((row) => ({ ...row, amount: String(row.amount) })),
  checks: {
    unconfirmed: Number(wire.checks.unconfirmed),
    suggested: Number(wire.checks.suggested),
    unassignedTotal: String(wire.checks.unassignedTotal),
    excludedTotal: String(wire.checks.excludedTotal),
    difference: String(wire.checks.difference),
  },
  final: wire.final
    ? { id: wire.final.id, finalizedAt: wire.final.finalizedAt, differs: wire.final.differs }
    : null,
});

const documentApprovalRoute = (kind: T.ApprovalDocumentKind, id: T.Id) =>
  API_ROUTES.approvalDocument
    .replace("{documentType}", APPROVAL_DOCUMENT_TYPES[kind])
    .replace("{id}", encodeURIComponent(id));

const toApprovalRule = (rule: Schemas["ApprovalRule"]): T.ApprovalRule => ({
  documentType: rule.documentType,
  mode: String(rule.mode).toLowerCase() as T.ApprovalMode,
  threshold: rule.threshold == null ? null : String(rule.threshold),
  levels: Number(rule.levels) === 2 ? 2 : 1,
  level1Approvers: [...rule.level1Approvers],
  level2Approvers: [...rule.level2Approvers],
});

const toDocumentApproval = (wire: Schemas["DocumentApprovalResponse"]): T.DocumentApproval => ({
  required: wire.required,
  request: wire.request
    ? {
        id: wire.request.id,
        status: String(wire.request.status).toLowerCase() as T.DocumentApprovalRequest["status"],
        levels: Number(wire.request.levels),
        step: (wire.request.step ?? null) as T.DocumentApprovalRequest["step"],
        mayAct: wire.request.mayAct,
        mine: wire.request.mine,
        submittedBy: wire.request.submittedBy,
        submittedAt: wire.request.submittedAt,
        verifiedBy: wire.request.verifiedBy ?? null,
        verifiedAt: wire.request.verifiedAt ?? null,
        approvedBy: wire.request.approvedBy ?? null,
        approvedAt: wire.request.approvedAt ?? null,
        returnedBy: wire.request.returnedBy ?? null,
        returnedAt: wire.request.returnedAt ?? null,
        returnReason: wire.request.returnReason ?? null,
      }
    : null,
});

const fillRoute = (template: string, value: string) =>
  template.replace(/\{[^}]+\}/, encodeURIComponent(value));

/** "Desktop · Chrome" — a family for the guide analytics, never the raw user agent. */
/** A report tag as the screens read it: supplied, or a company's own under its parent (ADR-0065). */
function reportTag(tag: Schemas["ReportTag"]): T.ReportTag {
  return {
    code: tag.code,
    label: tag.label,
    group: setup.accountGroup(tag.type),
    category: tag.category ?? null,
    parentCode: tag.parentCode ?? null,
    id: tag.id ?? null,
    archived: tag.archived ?? false,
  };
}

function deviceFamily(): string {
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent;
  const form = /Mobi|Android|iPhone|iPad/i.test(ua) ? "Mobile" : "Desktop";
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /Firefox\//.test(ua)
      ? "Firefox"
      : /Chrome\//.test(ua)
        ? "Chrome"
        : /Safari\//.test(ua)
          ? "Safari"
          : "Other";
  return `${form} · ${browser}`;
}

/**
 * HTTP implementation of RigoApi.
 *
 * **The transport is real; the surface is very nearly not yet** (build plan 2.6, review R6). All
 * but one member below refuses, and says which of two things it is waiting for: a route that does
 * not exist on `/api/v1` yet, or a route that does exist whose shape has not been reconciled with
 * the SPA's. The exception is `subscription.status` (ADR-0009), which is wired: a banner about
 * money owed to Rigo would be a lie if it came from a fixture.
 *
 * That is deliberate, and it is an improvement on what stood here. The previous version invented
 * routes — `/bills/submit`, `/accounts`, `/settings/numbering`, a dozen more — none of which the
 * API has. A member that quietly 404s reads as implemented; one that refuses by name enumerates the
 * remaining work. `UNSERVED` is that enumeration, and `routes.test.ts` holds the routes it names to
 * the ones the API actually publishes.
 *
 * What is finished here is everything underneath: the antiforgery handshake, the idempotency key,
 * and RFC 9457 problem details. The first member somebody wires works without touching any of it.
 */
export function createHttpApi(base: string): RigoApi {
  const http = createHttpClient(base);
  const bff = createHttpClient("/bff");

  /**
   * The session, and who the person may switch to. The list is a call to the identity service: its
   * outage takes the picker away, not the session of somebody already signed in.
   */
  let session: T.Session | undefined;
  const loadSession = async (): Promise<T.Session> => {
    const [wire, organisations] = await Promise.all([
      http.get<SessionWire>(API_ROUTES.session),
      bff.get<OrganisationWire[]>("/organisations").catch(() => []),
    ]);
    session = toSession(wire, organisations);
    return session;
  };

  /** The single read names no accounts, so the names come from the bank-account list. */
  const getTransfer = async (id: T.Id): Promise<T.FundTransfer> => {
    const [transfer, banks] = await Promise.all([
      http.get<setup.TransferViewWire>(fillRoute(API_ROUTES.transfer, id)),
      http.get<setup.BankAccountWire[]>(API_ROUTES.bankAccounts),
    ]);
    return setup.toTransferFromView(transfer, banks);
  };

  const getBill = async (id: T.Id): Promise<T.Bill> =>
    bills.toBill(await http.get<bills.BillDetailWire>(fillRoute(API_ROUTES.bill, id)));

  /**
   * Draft, then content: the API's own flow. Saving again revises the same draft, header and lines.
   */
  const saveBill = async (dto: T.NewBill): Promise<T.Bill> => {
    const header = bills.toNewBill(dto, session?.organization.currency || "NPR");
    let id = dto.id;
    if (id === undefined) {
      ({ id } = await http.post<{ id: string }>(API_ROUTES.bills, header));
    } else {
      await http.put(fillRoute(API_ROUTES.billHeader, id), header);
    }
    const taxCodes = setup.toTaxCodes(await http.get<setup.TaxCodeWire[]>(API_ROUTES.taxCodes));
    await http.put(fillRoute(API_ROUTES.bill, id), bills.toBillRevision(dto, taxCodes));
    return getBill(id);
  };

  const getDebitNote = async (id: T.Id): Promise<T.DebitNote> =>
    bills.toDebitNote(
      await http.get<bills.DebitNoteDetailWire>(fillRoute(API_ROUTES.debitNote, id)),
    );

  const getCreditNote = async (id: T.Id): Promise<T.CreditNote> =>
    sales.toCreditNote(
      await http.get<sales.DocumentDetailWire>(fillRoute(API_ROUTES.creditNote, id)),
    );

  const getReceipt = async (id: T.Id): Promise<T.Receipt> =>
    sales.toReceipt(await http.get<sales.ReceiptDetailWire>(fillRoute(API_ROUTES.receipt, id)));

  /** Takes the number and posts, once it's approved. A refusal here leaves it approved, to try again. */
  const issueApproved = async (kind: T.ApprovalDocumentKind, id: T.Id) => {
    if (kind === "bill") return void (await http.post(fillRoute(API_ROUTES.billApproval, id)));
    if (kind === "debitNote") {
      return void (await http.post(fillRoute(API_ROUTES.debitNoteApproval, id)));
    }
    if (kind === "fundTransfer") {
      return void (await http.post(fillRoute(API_ROUTES.transferPosting, id)));
    }
    if (kind === "journal") return void (await http.post(fillRoute(API_ROUTES.journalPosting, id)));
    if (kind === "payment") return void (await http.post(fillRoute(API_ROUTES.paymentPosting, id)));
    if (kind === "employeePayment") {
      return void (await http.post(fillRoute(API_ROUTES.employeeMovementPosting, id)));
    }
    if (kind === "purchaseOrder") {
      return void (await http.post(fillRoute(API_ROUTES.purchaseOrderSending, id)));
    }
    const [issue, posting, series] =
      kind === "creditNote"
        ? [API_ROUTES.creditNoteIssue, API_ROUTES.creditNotePosting, sales.CREDIT_NOTE_SERIES]
        : [API_ROUTES.invoiceIssue, API_ROUTES.invoicePosting, sales.INVOICE_SERIES];
    await http.post(`${fillRoute(issue, id)}?series=${series}`);
    await http.post(fillRoute(posting, id));
  };

  const getInvoice = async (id: T.Id): Promise<T.Invoice> =>
    sales.toInvoice(await http.get<sales.DocumentDetailWire>(fillRoute(API_ROUTES.invoice, id)));

  /** Refuses by name, saying which of the two jobs this member is waiting for. */
  const unserved = (name: keyof typeof UNSERVED) => (): never => {
    const route = UNSERVED[name];
    throw new Error(
      route === "no route"
        ? `api.${name} has no route on /api/v1 yet (build plan 2.6).`
        : `api.${name} is not wired: ${base}${route} exists, but the SPA's shape and the API's ` +
            `have not been reconciled (build plan 2.6).`,
    );
  };

  return {
    session: {
      get: loadSession,
      switchOrganization: async (organisationId) => {
        await bff.post<void>("/organisation", { organisationId });
        return loadSession();
      },
      openDemo: async () => {
        await bff.post<unknown>("/demo");
      },
      signOut: () => bff.post<void>("/logout"),
      updateProfile: async (input) => {
        await bff.put<void>("/profile", input);
      },
      changePassword: async (input) => {
        await bff.post<void>("/password", input);
      },
    },
    // Served, not waiting: the route exists, its shape is settled, and a billing banner cannot be
    // rendered from a fixture. Ungated and exempt from the 402 gate on the API side, so it keeps
    // answering while everything around it is refused — which is the whole point of it.
    subscription: {
      status: () => http.get<SubscriptionStatus>(API_ROUTES.subscriptionStatus),
      exportData: () => http.getFile(API_ROUTES.subscriptionExport),
      payBill: (billId, method) =>
        http.post<T.CheckoutOrder>(API_ROUTES.payBill, {
          billId: billId ?? null,
          method: method ?? null,
        }),
      checkoutOffer: (country) =>
        http.get<T.CheckoutOffer>(API_ROUTES.checkout, country ? { country } : undefined),
      quoteCheckout: (choice) => http.post<T.CheckoutQuote>(API_ROUTES.checkoutQuote, choice),
      placeOrder: (input) => http.post<T.CheckoutOrder>(API_ROUTES.checkout, input),
      confirmPayment: (checkoutId, callback) =>
        http.post<T.CheckoutResult>(fillRoute(API_ROUTES.checkoutConfirm, checkoutId), {
          callback,
        }),
      confirmReturn: (callback) =>
        http.post<T.CheckoutResult>(API_ROUTES.checkoutReturn, { callback }),
      account: () => http.get<T.SubscriptionAccount>(API_ROUTES.subscriptionAccount),
      scheduleChange: (input) =>
        http.post<T.SubscriptionAccount>(API_ROUTES.subscriptionChanges, input),
      undoChange: (changeId) =>
        http.post<T.SubscriptionAccount>(fillRoute(API_ROUTES.subscriptionChangeUndo, changeId)),
      updateBilling: (billing) =>
        http.put<T.SubscriptionAccount>(API_ROUTES.subscriptionBilling, billing),
    },
    // Served (ADR-0010): the guides and tickets are the platform's, forwarded by /api/v1/support.
    help: {
      status: () => http.get<T.HelpStatus>(API_ROUTES.helpStatus),
      home: () => http.get<T.HelpHome>(API_ROUTES.helpHome),
      category: (slug) => http.get<T.HelpCategoryPage>(fillRoute(API_ROUTES.helpCategory, slug)),
      article: async (slug) => {
        try {
          const article = await http.get<T.HelpArticle>(fillRoute(API_ROUTES.helpArticle, slug));
          return { kind: "article", article };
        } catch (error) {
          if (!(error instanceof HttpError)) throw error;
          const problem = error.problem as ApiError & {
            articleTitle?: string;
            requiredRole?: string;
          };
          // The gate, passed through: the guide exists and this reader may not open it.
          if (problem.status === 403 && problem.type.endsWith("/help_article_restricted")) {
            return {
              kind: "restricted",
              title: problem.articleTitle ?? "",
              requiredRole: problem.requiredRole ?? "",
              detail: problem.detail ?? problem.title,
            };
          }
          if (problem.status === 404) return { kind: "notFound" };
          throw error;
        }
      },
      search: (q) => http.get<T.HelpSearchHit[]>(API_ROUTES.helpSearch, { q }),
      recordRead: (articleSlug) =>
        http.post<{ readEventId: number | null }>(API_ROUTES.helpReadEvents, {
          articleSlug,
          device: deviceFamily(),
        }),
      recordReadDuration: (readEventId, durationSec) =>
        http.post<void>(fillRoute(API_ROUTES.helpReadBeacon, String(readEventId)), { durationSec }),
    },
    support: {
      categories: () => http.get<string[]>(API_ROUTES.supportCategories),
      tickets: (q) => http.get<T.SupportTicketSummary[]>(API_ROUTES.supportTickets, q),
      ticket: (id) => http.get<T.SupportTicketDetail>(fillRoute(API_ROUTES.supportTicket, id)),
      raise: (dto) => http.post<T.SupportTicketDetail>(API_ROUTES.supportTickets, dto),
      reply: (id, bodyHtml, attachmentKeys) =>
        http.post<T.SupportTicketDetail>(fillRoute(API_ROUTES.supportTicketMessages, id), {
          bodyHtml,
          attachmentKeys,
        }),
      stageFiles: (files) => {
        const form = new FormData();
        for (const file of files) form.append("files", file, file.name);
        return http.postForm<T.SupportStagedFile[]>(API_ROUTES.supportUploads, form);
      },
      attachmentUrl: (id) => base + fillRoute(API_ROUTES.supportAttachment, id),
    },
    overview: {
      get: async () => {
        // Each part comes from the route that owns it, and one a person may not read leaves a gap.
        const today = new Date().toISOString().slice(0, 10);
        const orNull = <R>(call: Promise<R>) => call.catch(() => null);
        const [figures, banks, ageing, invoices, overdue, draftBills] = await Promise.all([
          orNull(http.get<dashboards.OverviewWire>(API_ROUTES.overviewFigures)),
          orNull(http.get<setup.BankAccountWire[]>(API_ROUTES.bankAccounts)),
          orNull(
            http.get<dashboards.AgeingWire>(API_ROUTES.ageing, {
              role: "trade_receivable",
              asAt: today,
              buckets: "0,30,60,90",
            }),
          ),
          orNull(http.get<sales.DocumentPageWire>(API_ROUTES.invoices, { pageSize: "6" })),
          orNull(
            http.get<sales.DocumentPageWire>(API_ROUTES.invoices, {
              payment: "overdue",
              pageSize: "1",
            }),
          ),
          orNull(
            http.get<bills.BillPageWire>(API_ROUTES.bills, { approved: "false", pageSize: "1" }),
          ),
        ]);
        const draftInvoices = await orNull(
          http.get<sales.DocumentPageWire>(API_ROUTES.invoices, { issued: "false", pageSize: "1" }),
        );
        return dashboards.toOverview({
          today,
          figures,
          banks,
          ageing,
          recent: (invoices?.items ?? []).map(sales.toInvoiceSummary),
          draftInvoices: Number(draftInvoices?.total ?? 0),
          overdueInvoices: Number(overdue?.total ?? 0),
          draftBills: Number(draftBills?.total ?? 0),
        });
      },
    },
    invoices: {
      credits: async (id) =>
        sales.toInvoiceCredits(
          await http.get<Parameters<typeof sales.toInvoiceCredits>[0]>(
            fillRoute(API_ROUTES.invoiceCredits, id),
          ),
        ),
      useCredit: async (id, credit) => {
        await http.post(fillRoute(API_ROUTES.invoiceCredits, id), credit);
      },
      list: async (q) => {
        const page = await http.get<sales.DocumentPageWire>(
          API_ROUTES.invoices,
          sales.toDocumentQuery(q),
        );
        return {
          items: page.items.map(sales.toInvoiceSummary),
          next: page.next,
          total: Number(page.total),
        };
      },
      get: getInvoice,
      // Three calls, because the API's own flow is draft, then content. A refused revision leaves
      // an empty draft, which holds no number and can be revised again.
      saveDraft: async (dto) => {
        const header = sales.toNewInvoice(dto, session?.organization.currency || "NPR");
        let id = dto.id;
        if (id === undefined) {
          ({ id } = await http.post<{ id: string }>(API_ROUTES.invoices, header));
        } else {
          await http.put(fillRoute(API_ROUTES.invoiceHeader, id), header);
        }
        await http.put(fillRoute(API_ROUTES.invoice, id), sales.toRevision(dto));
        return getInvoice(id);
      },
      discardDraft: async (id) => {
        await http.del(fillRoute(API_ROUTES.invoice, id));
      },
      // Issuing freezes the document and posting writes the journal. They are two calls on the API,
      // and a refusal between them leaves an issued invoice for the second to retry.
      issue: async (id) => {
        await http.post(`${fillRoute(API_ROUTES.invoiceIssue, id)}?series=${sales.INVOICE_SERIES}`);
        await http.post(fillRoute(API_ROUTES.invoicePosting, id));
        return getInvoice(id);
      },
    },
    creditNotes: {
      list: async (q) => {
        const page = await http.get<sales.DocumentPageWire>(
          API_ROUTES.creditNotes,
          sales.toDocumentQuery(q),
        );
        return {
          items: page.items.map(sales.toCreditNoteSummary),
          next: page.next,
          total: Number(page.total),
        };
      },
      get: getCreditNote,
      saveDraft: async (dto) => {
        const today = new Date().toISOString().slice(0, 10);
        const corrects = dto.precedingInvoiceId
          ? await getInvoice(dto.precedingInvoiceId)
          : undefined;
        const header = sales.toNewCreditNote(
          dto,
          today,
          session?.organization.currency || "NPR",
          corrects?.number,
        );
        let id = dto.id;
        if (id === undefined) {
          ({ id } = await http.post<{ id: string }>(API_ROUTES.creditNotes, header));
        } else {
          await http.put(fillRoute(API_ROUTES.creditNoteHeader, id), header);
        }
        await http.put(
          fillRoute(API_ROUTES.creditNote, id),
          sales.toCreditNoteRevision(
            dto,
            corrects?.number ? { id: corrects.id, number: corrects.number } : undefined,
          ),
        );
        return getCreditNote(id);
      },
      discardDraft: async (id) => {
        await http.del(fillRoute(API_ROUTES.creditNote, id));
      },
      issue: async (id) => {
        await http.post(
          `${fillRoute(API_ROUTES.creditNoteIssue, id)}?series=${sales.CREDIT_NOTE_SERIES}`,
        );
        await http.post(fillRoute(API_ROUTES.creditNotePosting, id));
        return getCreditNote(id);
      },
    },
    documents: {
      requestPdf: async (kind, id) => {
        const route = {
          invoice: API_ROUTES.invoicePdf,
          creditNote: API_ROUTES.creditNotePdf,
          receipt: API_ROUTES.receiptPdf,
          quotation: API_ROUTES.quotationPdf,
          purchaseOrder: API_ROUTES.purchaseOrderPdf,
        }[kind];
        const { jobId } = await http.post<{ jobId: string }>(fillRoute(route, id));
        return jobId;
      },
      pdfStatus: async (jobId, fileName) => {
        const status = await http.get<Schemas["DocumentPdfStatus"]>(
          fillRoute(API_ROUTES.documentPdf, jobId),
          { fileName },
        );
        return {
          state: String(status.state).toLowerCase() as T.DocumentPdfStatus["state"],
          url: status.link ?? undefined,
        };
      },
      emailDraft: (target) =>
        http.get<Schemas["EmailDraftResponse"]>(fillRoute(EMAIL_ROUTES[target.kind], target.id)),
      sendEmail: async (target, email) => {
        const queued = await http.post<Schemas["EmailQueuedResponse"]>(
          fillRoute(EMAIL_ROUTES[target.kind], target.id),
          email,
        );
        return [...queued.recipients];
      },
      emails: async (of) => {
        const emails = await http.get<Array<Schemas["DocumentMailRecord"]>>(
          fillRoute(
            {
              invoice: API_ROUTES.invoiceEmails,
              quotation: API_ROUTES.quotationEmails,
              purchaseOrder: API_ROUTES.purchaseOrderEmails,
              customer: API_ROUTES.partyEmails,
            }[of.kind],
            of.id,
          ),
        );
        return emails.map((email) => ({
          id: email.id,
          subject: email.subject,
          sentAt: email.sentAt,
          recipients: email.recipients.map((recipient) => ({
            address: recipient.address,
            state: String(recipient.state).toLowerCase() as T.EmailState,
            reason: recipient.reason ?? undefined,
          })),
        }));
      },
    },
    receipts: {
      list: async (q) => {
        const page = await http.get<sales.ReceiptPageWire>(
          API_ROUTES.receipts,
          sales.toDocumentQuery(q),
        );
        return {
          items: page.items.map(sales.toReceiptSummary),
          next: page.next,
          total: Number(page.total),
        };
      },
      get: getReceipt,
      // Draft, applications, posting: the API's own order. A refusal at posting leaves a draft with
      // no number, and nothing that reached the ledger.
      create: async (dto) => {
        const banks = await http.get<setup.BankAccountWire[]>(API_ROUTES.bankAccounts);
        const bank = banks.find((b) => b.id === dto.bankAccountId);
        if (bank === undefined) throw new Error("That bank account no longer exists.");

        const { id } = await http.post<{ id: string }>(
          API_ROUTES.receipts,
          sales.toNewReceipt(dto, bank.ledgerAccountId, bank.currencyCode),
        );
        if (dto.applications.length > 0) {
          await http.put(fillRoute(API_ROUTES.receiptApplications, id), {
            applications: dto.applications.map((a) => ({
              invoiceId: a.invoiceId,
              amount: a.applied,
            })),
          });
        }
        const posted = await http.post<{ journalId: string }>(
          fillRoute(API_ROUTES.receiptPosting, id),
        );
        return { ...(await getReceipt(id)), journalId: posted.journalId };
      },
      openInvoicesFor: async (customerId) => {
        const page = await http.get<sales.DocumentPageWire>(API_ROUTES.invoices, {
          customerId,
          open: "true",
          pageSize: "100",
        });
        return page.items.map(sales.toInvoiceSummary);
      },
    },
    bills: {
      correctable: async (supplierId) => {
        const page = await http.get<bills.BillPageWire>(API_ROUTES.bills, {
          supplierId,
          approved: "true",
          pageSize: "100",
        });
        return page.items.map(bills.toBillSummary);
      },
      list: async (q) => {
        const page = await http.get<bills.BillPageWire>(API_ROUTES.bills, bills.toBillQuery(q));
        return {
          items: page.items.map(bills.toBillSummary),
          next: page.next,
          total: Number(page.total),
        };
      },
      kpis: async () =>
        bills.toBillFigures(await http.get<bills.BillFiguresWire>(API_ROUTES.billFigures)),
      get: getBill,
      saveDraft: saveBill,
      discardDraft: async (id) => {
        await http.del(fillRoute(API_ROUTES.bill, id));
      },
      submit: saveBill,
      credits: async (id) =>
        bills.toBillCredits(
          await http.get<Parameters<typeof bills.toBillCredits>[0]>(
            fillRoute(API_ROUTES.billCredits, id),
          ),
        ),
      useCredit: async (id, credit) => {
        await http.post(fillRoute(API_ROUTES.billCredits, id), credit);
      },
      approve: async (id) => {
        await http.post(fillRoute(API_ROUTES.billApproval, id));
        return getBill(id);
      },
    },
    fundTransfers: {
      list: async (q) => {
        const pageSize = q.pageSize ?? 25;
        const page = Number(q.cursor ?? "1");
        const found = await http.get<setup.TransferPageWire>(API_ROUTES.transfers, {
          q: q.q,
          from: q.from,
          to: q.to,
          minAmount: q.minAmount,
          page: String(page),
          pageSize: String(pageSize),
        });
        return {
          items: found.items.map(setup.toTransfer),
          next: page * pageSize < Number(found.total) ? String(page + 1) : null,
          total: Number(found.total),
        };
      },
      get: getTransfer,
      create: async (dto) => {
        const banks = await http.get<setup.BankAccountWire[]>(API_ROUTES.bankAccounts);
        const from = banks.find((b) => b.id === dto.fromBankAccountId);
        const { id } = await http.post<{ id: string }>(API_ROUTES.transfers, {
          transferredOn: dto.date,
          currencyCode: from?.currencyCode ?? "NPR",
          fromBankAccountId: dto.fromBankAccountId,
          toBankAccountId: dto.toBankAccountId,
          amount: dto.amount,
          reference: dto.reference || null,
        });
        return getTransfer(id);
      },
      post: async (id) => {
        await http.post(fillRoute(API_ROUTES.transferPosting, id));
        return getTransfer(id);
      },
    },
    debitNotes: {
      list: async (q) => {
        const pageSize = q.pageSize ?? 25;
        const page = q.cursor ? Math.floor(Number(q.cursor) / pageSize) + 1 : 1;
        const found = await http.get<bills.DebitNotePageWire>(API_ROUTES.debitNotes, {
          q: q.q,
          from: q.from,
          to: q.to,
          minAmount: q.minAmount,
          approved: q.status === "draft" ? "false" : q.status === "approved" ? "true" : undefined,
          page: String(page),
          pageSize: String(pageSize),
        });
        return {
          items: found.items.map(bills.toDebitNoteSummary),
          next: page * pageSize < Number(found.total) ? String(page + 1) : null,
          total: Number(found.total),
        };
      },
      get: getDebitNote,
      saveDraft: async (dto) => {
        const header = bills.toNewDebitNote(dto, session?.organization.currency || "NPR");
        let id = dto.id;
        if (id === undefined) {
          ({ id } = await http.post<{ id: string }>(API_ROUTES.debitNotes, header));
        } else {
          await http.put(fillRoute(API_ROUTES.debitNoteHeader, id), header);
        }
        const taxCodes = setup.toTaxCodes(await http.get<setup.TaxCodeWire[]>(API_ROUTES.taxCodes));
        await http.put(fillRoute(API_ROUTES.debitNote, id), bills.toBillRevision(dto, taxCodes));
        return getDebitNote(id);
      },
      discardDraft: async (id) => {
        await http.del(fillRoute(API_ROUTES.debitNote, id));
      },
      approve: async (id) => {
        await http.post(fillRoute(API_ROUTES.debitNoteApproval, id));
        return getDebitNote(id);
      },
    },
    quotations: {
      list: async (q) => {
        const pageSize = q.pageSize ?? 25;
        const page = q.cursor ? Math.floor(Number(q.cursor) / pageSize) + 1 : 1;
        const found = await http.get<sales.QuotationPageWire>(API_ROUTES.quotations, {
          q: q.q,
          status: q.status === undefined || q.status === "all" ? undefined : q.status,
          from: q.from,
          to: q.to,
          minAmount: q.minAmount,
          page: String(page),
          pageSize: String(pageSize),
        });
        return {
          items: found.items.map(sales.toQuotationSummary),
          next: page * pageSize < Number(found.total) ? String(page + 1) : null,
          total: Number(found.total),
          overview: {
            awaitingAmount: String(found.overview.awaitingAmount),
            awaitingCount: Number(found.overview.awaitingCount),
            acceptedAmount: String(found.overview.acceptedAmount),
            acceptedCount: Number(found.overview.acceptedCount),
            expiredCount: Number(found.overview.expiredCount),
          },
        };
      },
      get: async (id) =>
        sales.toQuotation(
          await http.get<sales.QuotationDetailWire>(fillRoute(API_ROUTES.quotation, id)),
        ),
      saveDraft: async (dto) => {
        const body = sales.toQuotationRequest(dto, session?.organization.currency || "NPR");
        let id = dto.id;
        if (id === undefined) {
          ({ id } = await http.post<{ id: string }>(API_ROUTES.quotations, body));
        } else {
          await http.put(fillRoute(API_ROUTES.quotation, id), body);
        }
        return sales.toQuotation(
          await http.get<sales.QuotationDetailWire>(fillRoute(API_ROUTES.quotation, id)),
        );
      },
      discardDraft: async (id) => {
        await http.del(fillRoute(API_ROUTES.quotation, id));
      },
      number: async (id) => void (await http.post(fillRoute(API_ROUTES.quotationNumber, id))),
      send: async (id) => void (await http.post(fillRoute(API_ROUTES.quotationSending, id))),
      respond: async (id, accepted) =>
        void (await http.post(fillRoute(API_ROUTES.quotationResponse, id), { accepted })),
      duplicate: async (id) =>
        (await http.post<{ id: string }>(fillRoute(API_ROUTES.quotationDuplicate, id))).id,
      convert: async (id) =>
        (await http.post<{ id: string }>(fillRoute(API_ROUTES.quotationConversion, id))).id,
    },
    purchaseOrders: {
      list: async (q) => {
        const pageSize = q.pageSize ?? 25;
        const page = q.cursor ? Math.floor(Number(q.cursor) / pageSize) + 1 : 1;
        const found = await http.get<bills.PurchaseOrderPageWire>(API_ROUTES.purchaseOrders, {
          q: q.q,
          status: q.status === undefined || q.status === "all" ? undefined : q.status,
          from: q.from,
          to: q.to,
          minAmount: q.minAmount,
          page: String(page),
          pageSize: String(pageSize),
        });
        return {
          items: found.items.map(bills.toPurchaseOrderSummary),
          next: page * pageSize < Number(found.total) ? String(page + 1) : null,
          total: Number(found.total),
          overview: {
            onOrderAmount: String(found.overview.onOrderAmount),
            onOrderCount: Number(found.overview.onOrderCount),
            toBillAmount: String(found.overview.toBillAmount),
            toBillCount: Number(found.overview.toBillCount),
            overdueCount: Number(found.overview.overdueCount),
          },
        };
      },
      get: async (id) =>
        bills.toPurchaseOrder(
          await http.get<bills.PurchaseOrderDetailWire>(fillRoute(API_ROUTES.purchaseOrder, id)),
        ),
      saveDraft: async (dto) => {
        const body = bills.toPurchaseOrderRequest(dto, session?.organization.currency || "NPR");
        let id = dto.id;
        if (id === undefined) {
          ({ id } = await http.post<{ id: string }>(API_ROUTES.purchaseOrders, body));
        } else {
          await http.put(fillRoute(API_ROUTES.purchaseOrder, id), body);
        }
        return bills.toPurchaseOrder(
          await http.get<bills.PurchaseOrderDetailWire>(fillRoute(API_ROUTES.purchaseOrder, id)),
        );
      },
      discardDraft: async (id) => {
        await http.del(fillRoute(API_ROUTES.purchaseOrder, id));
      },
      number: async (id) => void (await http.post(fillRoute(API_ROUTES.purchaseOrderNumber, id))),
      send: async (id) => void (await http.post(fillRoute(API_ROUTES.purchaseOrderSending, id))),
      confirm: async (id) =>
        void (await http.post(fillRoute(API_ROUTES.purchaseOrderConfirmation, id))),
      cancel: async (id) =>
        void (await http.post(fillRoute(API_ROUTES.purchaseOrderCancellation, id))),
      receive: async (id, receivedOn, lines) =>
        void (await http.post(fillRoute(API_ROUTES.purchaseOrderReceipts, id), {
          receivedOn,
          lines,
        })),
      convert: async (id) =>
        (await http.post<{ id: string }>(fillRoute(API_ROUTES.purchaseOrderConversion, id))).id,
    },
    payments: {
      list: async (q) => {
        const pageSize = q.pageSize ?? 25;
        const page = q.cursor ? Math.floor(Number(q.cursor) / pageSize) + 1 : 1;
        const found = await http.get<bills.PaymentPageWire>(API_ROUTES.payments, {
          q: q.q,
          from: q.from,
          to: q.to,
          minAmount: q.minAmount,
          page: String(page),
          pageSize: String(pageSize),
        });
        return {
          items: found.items.map(bills.toPaymentSummary),
          next: page * pageSize < Number(found.total) ? String(page + 1) : null,
          total: Number(found.total),
        };
      },
      get: async (id) =>
        bills.toPayment(await http.get<bills.PaymentDetailWire>(fillRoute(API_ROUTES.payment, id))),
      create: async (dto) => {
        const recorded = await http.post<{ id: string }>(
          API_ROUTES.payments,
          bills.toNewPayment(dto),
        );
        return bills.toPayment(
          await http.get<bills.PaymentDetailWire>(fillRoute(API_ROUTES.payment, recorded.id)),
        );
      },
      post: async (id) => void (await http.post(fillRoute(API_ROUTES.paymentPosting, id))),
      discard: async (id) => void (await http.del(fillRoute(API_ROUTES.payment, id))),
      openBills: async (supplierId) => {
        const page = await http.get<bills.BillPageWire>(API_ROUTES.bills, {
          supplierId,
          open: "true",
          pageSize: "100",
        });
        return page.items.map(bills.toBillSummary);
      },
    },
    journals: {
      list: async (q) => {
        const pageSize = q.pageSize ?? 25;
        const page = q.cursor ? Math.floor(Number(q.cursor) / pageSize) + 1 : 1;
        const found = await http.get<setup.JournalPageWire>(API_ROUTES.journals, {
          q: q.q,
          from: q.from,
          to: q.to,
          minAmount: q.minAmount,
          page: String(page),
          pageSize: String(pageSize),
        });
        return {
          items: found.items.map((row) => ({
            id: row.id,
            number: row.number ?? null,
            date: row.date,
            narration: row.narration,
            debitAccount: row.debitAccount ?? null,
            creditAccount: row.creditAccount ?? null,
            status: row.status as T.JournalStatus,
            amount: String(row.amount),
            isAdjusting: row.isAdjusting,
            reversesNextYear: row.reversesNextYear,
          })),
          next: page * pageSize < Number(found.total) ? String(page + 1) : null,
          total: Number(found.total),
        };
      },
      get: async (id) => {
        const journal = setup.toJournal(
          await http.get<setup.JournalWire>(fillRoute(API_ROUTES.journal, id)),
        );
        // The ledger holds a party's id; the name is the party module's. A voucher names few.
        const named = new Map(
          await Promise.all(
            [...new Set(journal.lines.flatMap((line) => line.partyId ?? []))].map(
              async (partyId) =>
                [
                  partyId,
                  (await http.get<setup.PartyWire>(fillRoute(API_ROUTES.party, partyId))).legalName,
                ] as const,
            ),
          ),
        );
        return {
          ...journal,
          lines: journal.lines.map((line) => ({
            ...line,
            partyName: line.partyId ? named.get(line.partyId) : undefined,
          })),
        };
      },
      saveDraft: async (draft) => {
        const body = setup.fromJournalDraft(draft);
        const saved = draft.id
          ? await http.put<{ id: string }>(fillRoute(API_ROUTES.journal, draft.id), body)
          : await http.post<{ id: string }>(API_ROUTES.journals, body);
        return saved.id;
      },
      post: async (id) =>
        setup.toJournal(
          await http.post<setup.JournalWire>(fillRoute(API_ROUTES.journalPosting, id)),
        ),
      discard: async (id) =>
        setup.toJournal(
          await http.post<setup.JournalWire>(fillRoute(API_ROUTES.journalDiscard, id)),
        ),
    },
    reporting: {
      frameworks: async () =>
        (await http.get<Array<Schemas["ReportFramework"]>>(API_ROUTES.reportFrameworks)).map(
          (framework) => ({
            code: framework.code,
            name: framework.name,
            tags: framework.tags.map(reportTag),
          }),
        ),
      addTag: async (framework, tag) =>
        reportTag(
          await http.post<Schemas["ReportTag"]>(
            API_ROUTES.reportTags.replace("{framework}", encodeURIComponent(framework)),
            tag,
          ),
        ),
      renameTag: async (framework, id, label) =>
        reportTag(
          await http.put<Schemas["ReportTag"]>(
            API_ROUTES.reportTag
              .replace("{framework}", encodeURIComponent(framework))
              .replace("{id}", encodeURIComponent(id)),
            { label },
          ),
        ),
      archiveTag: async (framework, id) => {
        await http.post(
          API_ROUTES.reportTagArchive
            .replace("{framework}", encodeURIComponent(framework))
            .replace("{id}", encodeURIComponent(id)),
          {},
        );
      },
      mapping: async (framework) =>
        (
          await http.get<Array<Schemas["AccountMapping"]>>(
            API_ROUTES.accountMapping.replace("{framework}", encodeURIComponent(framework)),
          )
        ).map((row) => ({
          accountId: row.accountId,
          code: row.code,
          name: row.name,
          group: setup.accountGroup(row.type),
          isActive: row.isActive,
          balance: String(row.balance),
          status: row.status.toLowerCase() as T.AccountMappingRow["status"],
          tagCode: row.tagCode ?? null,
          suggestedBecause: row.suggestedBecause ?? null,
          exclusionReason: row.exclusionReason ?? null,
        })),
      saveMapping: async (framework, decisions) => {
        await http.put(
          API_ROUTES.accountMapping.replace("{framework}", encodeURIComponent(framework)),
          { decisions },
        );
      },
      templates: async () =>
        (await http.get<Array<Schemas["ReportTemplateSummary"]>>(API_ROUTES.reportTemplates)).map(
          toTemplateSummary,
        ),
      template: async (code) =>
        toTemplateDetail(
          await http.get<Schemas["ReportTemplateDetail"]>(
            API_ROUTES.reportTemplate.replace("{code}", encodeURIComponent(code)),
          ),
        ),
      cloneTemplate: async (code) =>
        toTemplateDetail(
          await http.post<Schemas["ReportTemplateDetail"]>(
            API_ROUTES.reportTemplateClone.replace("{code}", encodeURIComponent(code)),
          ),
        ),
      saveTemplate: async (id, name, lines) =>
        toTemplateDetail(
          await http.put<Schemas["ReportTemplateDetail"]>(
            fillRoute(API_ROUTES.reportTemplateById, id),
            {
              name,
              lines,
            },
          ),
        ),
      deleteTemplate: async (id) => {
        await http.del(fillRoute(API_ROUTES.reportTemplateById, id));
      },
      previewTemplate: async (preview) =>
        toRenderedStatement(
          await http.post<Schemas["RenderedStatement"]>(API_ROUTES.reportTemplatePreview, preview),
        ),
      statement: async (query) => {
        const params: Record<string, string> = { template: query.templateCode, to: query.to };
        if (query.from) params.from = query.from;
        if (query.compareFrom) params.compareFrom = query.compareFrom;
        if (query.compareTo) params.compareTo = query.compareTo;
        return toRenderedStatement(
          await http.get<Schemas["RenderedStatement"]>(API_ROUTES.statement, params),
        );
      },
      finalize: async (query) => {
        await http.post(API_ROUTES.statementFinalization, query);
      },
      snapshot: async (id) =>
        toRenderedStatement(
          await http.get<Schemas["RenderedStatement"]>(fillRoute(API_ROUTES.statementSnapshot, id)),
        ),
      mappingHistory: async (framework, accountId) =>
        (
          await http.get<Array<Schemas["MappingChange"]>>(
            API_ROUTES.accountMappingHistory
              .replace("{framework}", encodeURIComponent(framework))
              .replace("{accountId}", encodeURIComponent(accountId)),
          )
        ).map((change) => ({
          oldTagCode: change.oldTagCode ?? null,
          newTagCode: change.newTagCode ?? null,
          exclusionReason: change.exclusionReason ?? null,
          changedAt: change.changedAt,
        })),
    },
    employees: {
      list: async () =>
        (await http.get<Array<Schemas["EmployeeLedgerRow"]>>(API_ROUTES.employees)).map((row) => ({
          id: row.id,
          name: row.name,
          taxId: row.taxId ?? null,
          balance: String(row.balance),
          lastMovement: row.lastMovement ?? null,
          isActive: row.isActive,
        })),
      record: async (movement) => {
        const posted = await http.post<Schemas["PostedEmployeeMovement"]>(
          API_ROUTES.employeeMovements,
          movement,
        );
        return { id: posted.id, number: posted.number ?? null };
      },
      drafts: async () =>
        (
          await http.get<Array<Schemas["EmployeeMovementResponse"]>>(
            API_ROUTES.employeeMovementDrafts,
          )
        ).map(toEmployeeMovement),
      get: async (id) =>
        toEmployeeMovement(
          await http.get<Schemas["EmployeeMovementResponse"]>(
            fillRoute(API_ROUTES.employeeMovement, id),
          ),
        ),
      post: async (id) => {
        await http.post(fillRoute(API_ROUTES.employeeMovementPosting, id));
      },
      discard: async (id) => {
        await http.del(fillRoute(API_ROUTES.employeeMovement, id));
      },
    },
    parties: {
      search: async (kind, q) => {
        const found = await http.get<setup.PartySummaryWire[]>(API_ROUTES.parties, {
          q,
          roles: setup.PARTY_ROLE[kind],
        });
        return found.map((party) => setup.toPartySummary(party, kind));
      },
      list: async (kind, q) => {
        const found = await http.get<setup.PartySummaryWire[]>(API_ROUTES.parties, {
          q,
          roles: setup.PARTY_ROLE[kind],
          limit: "100",
        });
        return found.map((party) => setup.toPartySummary(party, kind));
      },
      get: async (id) =>
        setup.toParty(await http.get<setup.PartyWire>(fillRoute(API_ROUTES.party, id))),
      details: async (id) =>
        setup.toPartyForm(await http.get<setup.PartyWire>(fillRoute(API_ROUTES.party, id))),
      create: async (form) =>
        setup.toParty(
          await http.post<setup.PartyWire>(API_ROUTES.parties, {
            ...setup.fromPartyForm(form),
            countryCode: "NP",
            roles: setup.PARTY_ROLE[form.kind],
          }),
        ),
      // PUT replaces the whole party, and this form does not show all of it: what it does not own
      // goes back as it came, so a customer who is also a supplier stays both.
      update: async (id, form) => {
        const current = await http.get<setup.PartyWire>(fillRoute(API_ROUTES.party, id));
        return setup.toParty(
          await http.put<setup.PartyWire>(fillRoute(API_ROUTES.party, id), {
            ...current,
            ...setup.fromPartyForm(form),
          }),
        );
      },
      standing: async (id, kind, asAt) =>
        setup.toPartyStanding(
          await http.get<setup.PartyStandingWire>(fillRoute(API_ROUTES.partyStanding, id), {
            side: setup.PARTY_ROLE[kind],
            asAt,
          }),
        ),
      statement: async (id, kind, from, to) =>
        setup.toPartyStatement(
          await http.get<setup.PartyStatementWire>(fillRoute(API_ROUTES.partyStatement, id), {
            side: setup.PARTY_ROLE[kind],
            from,
            to,
          }),
        ),
      reconciliations: async (id) =>
        (
          await http.get<setup.SupplierReconciliationWire[]>(
            fillRoute(API_ROUTES.partyReconciliations, id),
          )
        ).map(setup.toSupplierReconciliation),
      reconcile: async (id, statementDate, statementBalance) =>
        setup.toSupplierReconciliation(
          await http.post<setup.SupplierReconciliationWire>(
            fillRoute(API_ROUTES.partyReconciliations, id),
            { statementDate, statementBalance },
          ),
        ),
    },
    banking: {
      reconciliation: async (accountId, asAt) =>
        setup.toBankReconciliation(
          await http.get<setup.BankReconciliationWire>(
            fillRoute(API_ROUTES.bankReconciliation, accountId),
            { asAt },
          ),
        ),
      matches: async (accountId, asAt) =>
        (
          await http.get<setup.StatementMatchWire[]>(fillRoute(API_ROUTES.bankMatches, accountId), {
            asAt,
          })
        ).map(setup.toStatementMatch),
      candidates: async (lineId) =>
        (
          await http.get<setup.MatchCandidateWire[]>(
            fillRoute(API_ROUTES.statementMatchCandidates, lineId),
          )
        ).map(setup.toMatchCandidate),
      match: async (lineId, journalId, journalLineId) => {
        await http.post(fillRoute(API_ROUTES.statementMatch, lineId), { journalId, journalLineId });
      },
      matchSeveral: async (lineId, movements) => {
        await http.post(fillRoute(API_ROUTES.statementMatches, lineId), { movements });
      },
      locks: async (accountId) =>
        (
          await http.get<Array<Schemas["ReconciliationLock"]>>(
            fillRoute(API_ROUTES.reconciliationLocks, accountId),
          )
        ).map(setup.toReconciliationLock),
      lock: async (accountId, asAt, statementBalance) => {
        await http.post(fillRoute(API_ROUTES.reconciliationLocks, accountId), {
          asAt,
          ...(statementBalance === null ? {} : { statementBalance }),
        });
      },
      reopen: async (lockId) => {
        await http.post(fillRoute(API_ROUTES.reconciliationReopen, lockId));
      },
      createEntry: async (lineId, input) => {
        const entry = await http.post<{ number: string | null; matchProblem: string | null }>(
          fillRoute(API_ROUTES.statementEntry, lineId),
          { accountId: input.accountId, memo: input.memo, movements: input.movements },
        );
        return { number: entry.number ?? "", matchProblem: entry.matchProblem };
      },
      unmatch: async (matchId) => {
        await http.del(fillRoute(API_ROUTES.statementUnmatch, matchId));
      },
      importStatement: async ({ accountId, fileName, content, format }) =>
        setup.toStatementImport(
          await http.post<setup.StatementImportWire>(
            format === null
              ? fillRoute(API_ROUTES.bankStatementOfx, accountId)
              : fillRoute(API_ROUTES.bankStatement, accountId),
            format === null ? { fileName, content } : { fileName, content, format },
          ),
        ),
    },
    billingItems: {
      list: async ({ q, accountId, category }) =>
        (
          await http.get<setup.BillingItemWire[]>(API_ROUTES.billingItems, {
            q: q || undefined,
            accountId,
            category: category && setup.billingItemCategoryQuery(category),
          })
        ).map(setup.toBillingItem),
      create: async (form) =>
        setup.toBillingItem(
          await http.post<setup.BillingItemWire>(
            API_ROUTES.billingItems,
            setup.fromBillingItemForm(form),
          ),
        ),
      update: async (id, form) =>
        setup.toBillingItem(
          await http.put<setup.BillingItemWire>(
            fillRoute(API_ROUTES.billingItem, id),
            setup.fromBillingItemForm(form),
          ),
        ),
      remove: async (id) => {
        await http.del(fillRoute(API_ROUTES.billingItem, id));
      },
    },
    accounts: {
      rename: async (id, name) => {
        await http.put(fillRoute(API_ROUTES.chartAccountName, id), { name });
      },
      list: async () =>
        setup.toAccounts(await http.get<setup.AccountWire[]>(API_ROUTES.chartAccounts)),
      chart: async () => {
        const wire = await http.get<Schemas["ChartOverview"]>(API_ROUTES.chart);
        return {
          accounts: wire.accounts.map(setup.toAccount),
          ledgerHasPostings: wire.ledgerHasPostings,
        };
      },
      create: async (form) =>
        setup.toAccount(
          await http.post<setup.AccountWire>(API_ROUTES.chartAccounts, {
            code: form.code.trim(),
            name: form.name.trim(),
            type: setup.accountType(form.group),
            subtype: form.subtype,
            statementLine: form.statementLine,
          }),
        ),
      update: async (id, edit) => {
        await http.put(fillRoute(API_ROUTES.chartAccount, id), {
          name: edit.name.trim(),
          subtype: edit.subtype,
          statementLine: edit.statementLine,
        });
      },
      usage: async (id) => {
        const wire = await http.get<Schemas["AccountUsage"]>(
          fillRoute(API_ROUTES.chartAccountUsage, id),
        );
        return {
          accountId: wire.accountId,
          posted: String(wire.posted),
          references: wire.references.map(setup.toReference),
        };
      },
      remove: async (id) => {
        await http.del(fillRoute(API_ROUTES.chartAccount, id));
      },
      setActive: async (id, active) => {
        await http.put(fillRoute(API_ROUTES.chartAccountActive, id), { active });
      },
      templates: async () =>
        (await http.get<Schemas["CoaTemplateSummary"][]>(API_ROUTES.chartTemplates)).map(
          setup.toTemplate,
        ),
      template: async (code, version) =>
        setup.toTemplateDetail(
          await http.get<Schemas["CoaTemplateDetail"]>(
            API_ROUTES.chartTemplateDetail
              .replace("{code}", encodeURIComponent(code))
              .replace("{version}", String(version)),
          ),
        ),
      replacePreview: async () => {
        const wire = await http.get<Schemas["ReplacePreview"]>(API_ROUTES.chartTemplatePreview);
        return {
          ledgerHasPostings: wire.ledgerHasPostings,
          replaceable: Number(wire.replaceable),
          createdByCompany: Number(wire.createdByCompany),
          blocking: wire.blocking.map(setup.toReference),
        };
      },
      applyTemplate: async (code, version, excludeCodes) => {
        const wire = await http.post<Schemas["TemplateApplied"]>(API_ROUTES.chartTemplate, {
          code,
          version,
          excludeCodes,
        });
        return {
          code: wire.code,
          version: Number(wire.version),
          removed: wire.removed,
          added: wire.added,
        };
      },
      bankAccounts: async () =>
        (await http.get<setup.BankAccountWire[]>(API_ROUTES.bankAccounts)).map(setup.toBankAccount),
      bankLedgerAccounts: async () => {
        // The bank and cash range (ADR-0066): accounts on "Cash and cash equivalents" no bank account holds yet.
        const [chart, banks] = await Promise.all([
          http.get<setup.AccountWire[]>(API_ROUTES.chartAccounts),
          http.get<setup.BankAccountWire[]>(API_ROUTES.bankAccounts),
        ]);
        const taken = new Set(banks.map((bank) => bank.ledgerAccountId));
        return setup
          .toAccounts(chart)
          .filter((a) => a.statementLine === "cash_and_cash_equivalents" && !taken.has(a.id));
      },
      createBankAccount: async (input) => {
        const orNull = (value: string) => (value.trim() === "" ? null : value.trim());
        const opening = orNull(input.openingBalance);
        const created = await http.post<{ openingBalanceProblem?: string | null }>(
          API_ROUTES.bankAccounts,
          {
            ledgerAccountId: input.ledgerAccountId,
            ledgerCode: input.ledgerAccountId === null ? orNull(input.ledgerCode) : null,
            name: input.name.trim(),
            currencyCode: "NPR",
            bankName: orNull(input.bankName),
            accountNumber: orNull(input.accountNumber),
            branch: orNull(input.branch),
            openingBalance: opening,
            openingDate: opening === null ? null : input.openingDate,
          },
        );
        return { openingBalanceProblem: created.openingBalanceProblem ?? null };
      },
    },
    approvals: {
      // A draft is what awaits approval on the API, so the queue is the drafts of each kind this
      // person may act on. Oldest first: whoever has waited longest.
      queue: async () => {
        const can = (permission: T.Permission) =>
          session?.permissions.includes(permission) ?? false;
        const requests = can("accounting.approvals.act")
          ? await http.get<Array<Schemas["ApprovalItemResponse"]>>(API_ROUTES.approvalQueue)
          : [];
        return requests.map((request): T.ApprovalItem => {
          const kind = APPROVAL_KINDS[request.documentType] ?? "invoice";
          return {
            id: `request:${kind}:${request.id}:${request.documentId}`,
            documentId: request.documentId,
            documentNumber: "Draft",
            partyName: request.partyName,
            kind,
            submittedBy: request.submittedBy,
            submittedAt: request.submittedAt,
            step: request.step as T.ApprovalItem["step"],
            amount: String(request.amount),
            actionLabel: request.step as T.ApprovalItem["actionLabel"],
          };
        });
      },
      act: async (id, action, note) => {
        const [, kind, requestId = "", documentId = ""] = id.split(":");
        if (action === "return") {
          await http.post(fillRoute(API_ROUTES.approvalReturn, requestId), { reason: note ?? "" });
        } else if (action === "verify") {
          await http.post(fillRoute(API_ROUTES.approvalVerification, requestId));
        } else {
          await http.post(fillRoute(API_ROUTES.approvalApproval, requestId));
          // A year's close isn't issued by approving it: closing is its own step, with its own reason.
          if (kind !== "yearClose") await issueApproved(kind as T.ApprovalDocumentKind, documentId);
        }
      },
      rules: async () =>
        (await http.get<Array<Schemas["ApprovalRule"]>>(API_ROUTES.approvalRules)).map(
          toApprovalRule,
        ),
      saveRules: async (rules, reason) =>
        (
          await http.put<Array<Schemas["ApprovalRule"]>>(API_ROUTES.approvalRules, {
            rules: rules.map((rule) => ({
              ...rule,
              // Only "above" has one, and a value left from before would be refused.
              threshold: rule.mode === "above" ? rule.threshold : null,
            })),
            reason,
          })
        ).map(toApprovalRule),
      document: async (kind, id) =>
        toDocumentApproval(
          await http.get<Schemas["DocumentApprovalResponse"]>(documentApprovalRoute(kind, id)),
        ),
      submit: async (kind, id) =>
        toDocumentApproval(
          await http.post<Schemas["DocumentApprovalResponse"]>(
            fillRoute(SUBMISSION_ROUTES[kind], id),
          ),
        ),
    },
    reports: {
      generalLedger: async ({ accountId, from, to, q, partyId, minAmount }) => {
        const wire = await http.get<reports.GeneralLedgerWire>(API_ROUTES.generalLedger, {
          accountId,
          from,
          to,
          q,
          partyId,
          minAmount,
        });
        return reports.toGeneralLedger(wire);
      },
      ledgerDocuments: async ({ accountId, from, to, q }) =>
        reports.toLedgerDocuments(
          await http.get<reports.LedgerDocumentsWire>(API_ROUTES.ledgerDocuments, {
            accountId,
            from,
            to,
            q,
          }),
        ),
      trialBalanceAsOn: async (asOn) =>
        reports.toTrialBalanceAsOn(
          await http.get<reports.TrialBalanceAsOnWire>(API_ROUTES.trialBalanceAsOn, { asOn }),
        ),
      cashFlow: async (from, to) =>
        reports.toCashFlow(await http.get<reports.CashFlowWire>(API_ROUTES.cashFlow, { from, to })),
      ageing: async (kind, asAt) =>
        reports.toAgeing(
          await http.get<reports.AgeingWire>(API_ROUTES.ageing, {
            role: kind === "receivable" ? "trade_receivable" : "trade_payable",
            asAt,
            buckets: "0,30,60,90",
          }),
        ),
      receivableAnalysis: async (asAt) =>
        reports.toReceivableAnalysis(
          await http.get<reports.ReceivableAnalysisWire>(API_ROUTES.receivableAnalysis, { asAt }),
        ),
      payableAnalysis: async (asAt) =>
        reports.toPayableAnalysis(
          await http.get<reports.PayableAnalysisWire>(API_ROUTES.payableAnalysis, { asAt }),
        ),
      openItems: async (kind, dueFrom, dueTo) =>
        reports.toOpenItems(
          await http.get<reports.OpenItemsWire>(API_ROUTES.openItems, {
            side: setup.PARTY_ROLE[kind],
            dueFrom,
            dueTo,
          }),
        ),
      vatReturn: async (from, to) =>
        reports.toVatReturn(
          await http.get<reports.VatReturnWire>(API_ROUTES.vatReturn, { from, to }),
        ),
      withholding: async (from, to) => {
        const [register, customers] = await Promise.all([
          http.get<reports.WithholdingWire>(API_ROUTES.withholdingRegister, { from, to }),
          http.get<reports.WithheldByCustomersWire>(API_ROUTES.withheldByCustomers, { from, to }),
        ]);
        return reports.toWithholding(register, customers);
      },
      exportToExcel: (report) => http.postForFile(API_ROUTES.reportExport, report),
      // The screen names fiscal years by label, and the statement is asked for by their dates.
      profitAndLoss: async ({ period, compare }) => {
        const years = setup.toFiscalYears(
          await http.get<setup.FiscalYearWire[]>(API_ROUTES.fiscalYears),
          new Date().toISOString().slice(0, 10),
        );
        const statement = async (label: string | undefined) => {
          const year = years.find((y) => y.label === label);
          return year === undefined
            ? []
            : http.get<dashboards.MovementWire[]>(API_ROUTES.incomeStatement, {
                from: year.start,
                to: year.end,
              });
        };
        const [current, prior] = await Promise.all([statement(period), statement(compare)]);
        return dashboards.toProfitAndLoss({ period, compare: compare ?? null, current, prior });
      },
    },
    settings: {
      paymentOptions: async () =>
        (await http.get<Array<Schemas["PaymentOption"]>>(API_ROUTES.paymentOptions)).map(
          sales.toPaymentOption,
        ),
      savePaymentOption: async ({ id, ...option }) =>
        sales.toPaymentOption(
          id
            ? await http.put<Schemas["PaymentOption"]>(
                fillRoute(API_ROUTES.paymentOption, id),
                sales.toPaymentOptionRequest(option),
              )
            : await http.post<Schemas["PaymentOption"]>(
                API_ROUTES.paymentOptions,
                sales.toPaymentOptionRequest(option),
              ),
        ),
      removePaymentOption: async (id) => {
        await http.del(fillRoute(API_ROUTES.paymentOption, id));
      },
      additionalCharges: async () =>
        (await http.get<Array<Schemas["AdditionalCharge"]>>(API_ROUTES.additionalCharges)).map(
          sales.toAdditionalCharge,
        ),
      saveAdditionalCharge: async ({ id, ...charge }) =>
        sales.toAdditionalCharge(
          id
            ? await http.put<Schemas["AdditionalCharge"]>(
                fillRoute(API_ROUTES.additionalCharge, id),
                sales.toAdditionalChargeRequest(charge),
              )
            : await http.post<Schemas["AdditionalCharge"]>(
                API_ROUTES.additionalCharges,
                sales.toAdditionalChargeRequest(charge),
              ),
        ),
      removeAdditionalCharge: async (id) => {
        await http.del(fillRoute(API_ROUTES.additionalCharge, id));
      },
      invoicing: async () => {
        const wire = await http.get<Schemas["InvoiceSetting"]>(API_ROUTES.invoiceSetting);
        return { ...wire, footerText: wire.footerText ?? "" };
      },
      saveInvoicing: async (setting) => {
        const wire = await http.put<Schemas["InvoiceSetting"]>(API_ROUTES.invoiceSetting, setting);
        return { ...wire, footerText: wire.footerText ?? "" };
      },
      company: async () => setup.toCompany(await http.get<setup.CompanyWire>(API_ROUTES.company)),
      saveCompany: async (profile) =>
        setup.toCompany(
          await http.put<setup.CompanyWire>(API_ROUTES.company, setup.fromCompany(profile)),
        ),
      setup: () => http.get<T.SetupStatus>(API_ROUTES.setup),
      runSetup: () => http.post<T.SetupStatus>(API_ROUTES.setup),
      setupSteps: () => http.get<T.SetupStepState[]>(API_ROUTES.setupSteps),
      saveSetupStep: async (step, status) => {
        await http.put(API_ROUTES.setupStep.replace("{step}", encodeURIComponent(step)), {
          status,
        });
      },
      numbering: async () =>
        setup.toNumbering(await http.get<setup.NumberingWire>(API_ROUTES.numbering)),
      saveNumbering: async (input) =>
        setup.toNumbering(await http.put<setup.NumberingWire>(API_ROUTES.numbering, input)),
      fiscalYears: async () =>
        setup.toFiscalYears(
          await http.get<setup.FiscalYearWire[]>(API_ROUTES.fiscalYears),
          new Date().toISOString().slice(0, 10),
        ),
      yearChecklist: async (fiscalYearId) => {
        const wire = await http.get<Schemas["YearChecklistResponse"]>(
          fillRoute(API_ROUTES.yearCloseChecks, fiscalYearId),
        );
        return {
          fiscalYearCode: wire.fiscalYearCode,
          endsOn: wire.endsOn,
          locked: wire.locked,
          workstreams: wire.workstreams.map((w) => ({
            id: w.id,
            name: w.name,
            evidenceHint: w.evidenceHint,
            status: w.status as T.YearCloseWorkstream["status"],
            note: w.note ?? null,
            evidence: w.evidence ?? null,
            actedBy: w.actedBy ?? null,
            actedAt: w.actedAt ?? null,
            items: w.items.map((i) => ({
              key: i.key,
              kind: i.kind,
              documentId: i.documentId,
              number: i.number ?? null,
              date: i.date ?? null,
              party: i.party,
              amount: String(i.amount),
              why: i.why,
              reviewed: i.reviewed,
            })),
            moreItems: Number(w.moreItems),
          })),
          log: wire.log.map((l) => ({
            id: l.id,
            loggedAt: l.loggedAt,
            loggedBy: l.loggedBy,
            action: l.action,
            detail: l.detail,
            refused: l.refused,
          })),
        };
      },
      reviewCheckItem: async (fiscalYearId, workstream, key, reviewed) =>
        void (await http.put(
          fillRoute(API_ROUTES.yearCloseItems, fiscalYearId).replace("{workstream}", workstream),
          { key, reviewed },
        )),
      markCheckDone: async (fiscalYearId, workstream, done) =>
        void (await http.post(
          fillRoute(API_ROUTES.yearCloseDone, fiscalYearId).replace("{workstream}", workstream),
          { evidence: done.evidence, note: done.note ?? null },
        )),
      markCheckNotApplicable: async (fiscalYearId, workstream, note) =>
        void (await http.post(
          fillRoute(API_ROUTES.yearCloseNotApplicable, fiscalYearId).replace(
            "{workstream}",
            workstream,
          ),
          { note },
        )),
      reopenCheck: async (fiscalYearId, workstream) =>
        void (await http.post(
          fillRoute(API_ROUTES.yearCloseReopen, fiscalYearId).replace("{workstream}", workstream),
        )),
      yearEndMethod: async () =>
        (await http.get<Schemas["YearEndMethodResponse"]>(API_ROUTES.yearEndMethod))
          .method as T.YearEndMethod,
      saveYearEndMethod: async (method) =>
        void (await http.put(API_ROUTES.yearEndMethod, { method })),
      yearEndPreview: async (fiscalYearId) => {
        const wire = await http.get<Schemas["YearEndPreview"]>(
          fillRoute(API_ROUTES.yearEnd, fiscalYearId),
        );
        return {
          fiscalYearId: wire.fiscalYearId,
          fiscalYearCode: wire.fiscalYearCode,
          nextFiscalYearId: wire.nextFiscalYearId ?? null,
          opensOn: wire.opensOn ?? null,
          periodsStillOpen: wire.periodsStillOpen.map((period) => ({
            periodId: period.periodId,
            ordinal: Number(period.ordinal),
            startsOn: period.startsOn,
            endsOn: period.endsOn,
            status: Number(period.status),
          })),
          carriedForward: wire.carriedForward.map((line) => ({
            accountId: line.accountId,
            code: line.code,
            name: line.name,
            amount: String(line.amount),
          })),
          result: String(wire.result),
          canCarryForward: wire.canCarryForward,
          method: wire.method as T.YearEndMethod,
          closure: wire.closure ? closureFrom(wire.closure) : null,
          closures: (wire.closures ?? []).map(closureFrom),
          reopening: wire.reopening
            ? {
                id: wire.reopening.id,
                reason: wire.reopening.reason,
                reopenedAt: wire.reopening.reopenedAt,
                periods: wire.reopening.periods.map(monthFrom),
                accounts: wire.reopening.accounts.map((account) => ({
                  accountId: account.accountId,
                  code: account.code,
                  name: account.name,
                })),
              }
            : null,
          difference: wire.difference ? wire.difference.map(changeFrom) : null,
          periods: (wire.periods ?? []).map(monthFrom),
          revision: Number(wire.revision ?? 0),
        };
      },
      closeYear: async (fiscalYearId, { reason, journalId, manual, revision }) =>
        void (await (journalId || manual
          ? http.post(fillRoute(API_ROUTES.yearEndManual, fiscalYearId), {
              journalId,
              reason,
              revision,
            })
          : http.post(fillRoute(API_ROUTES.yearEnd, fiscalYearId), { reason, revision }))),
      yearCloseGates: async (fiscalYearId) => {
        const wire = await http.get<Schemas["YearCloseGates"]>(
          fillRoute(API_ROUTES.yearCloseGates, fiscalYearId),
        );
        return {
          fiscalYearId: wire.fiscalYearId,
          documentId: wire.documentId,
          softLocked: wire.softLocked,
          closed: wire.closed,
          gates: wire.gates.map((g) => ({
            key: g.key as T.YearCloseGateKey,
            name: g.name,
            passed: g.passed,
            detail: g.detail,
          })),
          openingBalanceEquity: String(wire.openingBalanceEquity),
        };
      },
      setYearSoftLock: async (fiscalYearId, on) =>
        void (await http.put(fillRoute(API_ROUTES.yearSoftLock, fiscalYearId), { on })),
      withdrawYearClose: async (fiscalYearId, reason) =>
        void (await http.post(fillRoute(API_ROUTES.yearCloseWithdrawal, fiscalYearId), { reason })),
      yearWorkpaper: async (fiscalYearId) => {
        const wire = await http.get<Schemas["YearWorkpaper"]>(
          fillRoute(API_ROUTES.yearWorkpaper, fiscalYearId),
        );
        return {
          fiscalYearCode: wire.fiscalYearCode,
          endsOn: wire.endsOn,
          revision: Number(wire.revision),
          postedProfit: String(wire.postedProfit),
          draftsEffect: String(wire.draftsEffect),
          profitIfDraftsPost: String(wire.profitIfDraftsPost),
          adjustments: wire.adjustments.map((a) => ({
            id: a.id,
            number: a.number ?? null,
            date: a.date,
            narration: a.narration,
            status: a.status as T.YearWorkpaper["adjustments"][number]["status"],
            reversesNextYear: a.reversesNextYear,
            profitEffect: String(a.profitEffect),
          })),
          trialBalance: wire.trialBalance.map((b) => ({
            accountId: b.accountId,
            code: b.code,
            name: b.name,
            unadjusted: String(b.unadjusted),
            adjustments: String(b.adjustments),
            adjusted: String(b.adjusted),
          })),
        };
      },
      exportYearWorkpaper: (fiscalYearId) =>
        http.postForFile(fillRoute(API_ROUTES.yearWorkpaperExport, fiscalYearId), {}),
      reopenYear: async (fiscalYearId, reopen) =>
        void (await http.post(fillRoute(API_ROUTES.yearReopen, fiscalYearId), reopen)),
      openNextYear: async (fiscalYearId) =>
        void (await http.post(fillRoute(API_ROUTES.nextFiscalYear, fiscalYearId))),
      hardClosePeriod: async (periodId, reason) =>
        void (await http.post(fillRoute(API_ROUTES.periodClose, periodId), { hard: true, reason })),
      yearEnd: unserved("settings.yearEnd"),
      yearEndAdvance: unserved("settings.yearEndAdvance"),
      yearEndResolve: unserved("settings.yearEndResolve"),
      // The matrix is the catalogue; only the member counts are a fact about this organisation.
      roles: async () => {
        const users = await http.get<Schemas["UserRow"][]>(API_ROUTES.users).catch(() => []);
        return ROLE_NAMES.map((name) => ({
          name,
          builtIn: true,
          permissions: ROLE_PERMISSIONS[name],
          members: users.filter((user) => user.role === name).length,
        }));
      },
      users: async () =>
        (await http.get<Schemas["UserRow"][]>(API_ROUTES.users)).map((user) => ({
          id: user.id,
          fullName: user.fullName,
          email: user.email ?? "",
          role: user.role as T.RoleName,
          status: user.invited
            ? ("invited" as const)
            : user.active
              ? ("active" as const)
              : ("disabled" as const),
          accessExpires: user.accessExpires ?? null,
        })),
      resendInvitation: async (id) => {
        await http.post(fillRoute(API_ROUTES.userInvitation, id));
      },
      inviteUser: async ({ fullName, email, role, accessExpires }) => {
        const user = await http.post<Schemas["UserRow"]>(API_ROUTES.users, {
          email,
          role,
          accessExpires,
          fullName,
        });
        return {
          id: user.id,
          fullName: user.fullName || fullName,
          email: user.email ?? email,
          role,
          status: user.invited ? "invited" : "active",
          accessExpires,
        };
      },
      audit: async ({ q, kind }) => {
        const rows = await http.get<Schemas["AuditRow"][]>(API_ROUTES.audit, {
          q,
          kind: kind === undefined || kind === "all" ? undefined : kind,
        });
        return rows.map((row, index) => ({
          id: String(index),
          at: row.at,
          user: row.user,
          kind: row.kind as T.AuditEntry["kind"],
          document: row.document ?? undefined,
          detail: row.detail,
        }));
      },
      approvalPolicy: async () =>
        (await http.get<Schemas["ApprovalPolicy"][]>(API_ROUTES.approvalPolicy)).map((row) => ({
          documentType: row.documentType,
          separateApprover: row.separateApprover,
        })),
      setApprovalPolicy: async ({ documentType, separateApprover, reason }) => {
        await http.put(fillRoute(API_ROUTES.approvalPolicyFor, documentType), {
          separateApprover,
          reason,
        });
      },
      saveTaxNames: async (names) => {
        const saved = await http.put<components["schemas"]["TaxNames"]>(API_ROUTES.taxNames, names);
        return {
          registration: saved.registration,
          valueAdded: saved.valueAdded,
          withholding: saved.withholding,
          packName: saved.packName ?? null,
        };
      },
      taxCodes: async () => {
        const [codes, roles] = await Promise.all([
          http.get<setup.TaxCodeWire[]>(API_ROUTES.taxCodes),
          // Someone who manages tax may not read the chart, and the codes are still worth showing.
          http.get<{ mapped: Record<string, string> }>(API_ROUTES.chartRoles).catch(() => null),
        ]);
        return setup.toTaxCodes(codes, roles?.mapped ?? {});
      },
      renameTaxCode: async (id, name) => {
        await http.put(fillRoute(API_ROUTES.taxCodeName, id), { name });
      },
      renameWithholdingCode: async (code, name) => {
        await http.put(API_ROUTES.withholdingCodeName.replace("{code}", encodeURIComponent(code)), {
          name,
        });
      },
      branding: async () =>
        setup.toBranding(await http.get<Schemas["DocumentBranding"]>(API_ROUTES.branding)),
      saveBranding: async (branding) =>
        setup.toBranding(
          await http.put<Schemas["DocumentBranding"]>(API_ROUTES.branding, branding),
        ),
      saveBrandingLogo: async (base64) =>
        setup.toBranding(
          await http.put<Schemas["DocumentBranding"]>(API_ROUTES.brandingLogo, { base64 }),
        ),
      removeBrandingLogo: async () =>
        setup.toBranding(await http.del<Schemas["DocumentBranding"]>(API_ROUTES.brandingLogo)),
      emailTemplates: () =>
        http
          .get<Array<Schemas["EmailTemplateResponse"]>>(API_ROUTES.emailTemplates)
          .then((list) => list.map((t) => ({ ...t, placeholders: [...t.placeholders] }))),
      saveEmailTemplate: async ({ kind, subject, body }) => {
        const saved = await http.put<Schemas["EmailTemplateResponse"]>(
          fillRoute(API_ROUTES.emailTemplate, kind),
          { subject, body },
        );
        return { ...saved, placeholders: [...saved.placeholders] };
      },
      resetEmailTemplate: async (kind) => {
        const reset = await http.del<Schemas["EmailTemplateResponse"]>(
          fillRoute(API_ROUTES.emailTemplate, kind),
        );
        return { ...reset, placeholders: [...reset.placeholders] };
      },
      countryPack: async () => {
        const wire = await http.get<Schemas["CountryPackResponse"]>(API_ROUTES.countryPack);
        return {
          ...wire,
          fiscalYearStartsMonth: Number(wire.fiscalYearStartsMonth),
          fiscalYearStartsDay: Number(wire.fiscalYearStartsDay),
          roundingScale: Number(wire.roundingScale),
        };
      },
      withholdingCodes: async () =>
        (
          await http.get<Array<{ code: string; name: string; ratePercent: unknown }>>(
            API_ROUTES.withholdingCodes,
          )
        ).map((row) => ({
          code: row.code,
          name: row.name,
          ratePct: row.ratePercent === null ? null : String(Number(row.ratePercent)),
        })),
    },
  };
}
